import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { withProduct } from '../../core/route-map.js';
import { toast } from '../../components/toast.js';
import { button } from '../../components/button.js';
import { fillMarkdown } from '../../components/markdown.js';
import { disposeCharts } from '../../components/markdown-chart.js';
import { clipAtWord, insightCardPreview } from './copy.js';
import { correctionBody } from '../../core/parity.js';

/**
 * First-read block with the same correction and save actions as the detail dialog.
 * @param {object|null|undefined} insights
 * @param {string} [productId]
 */
export function firstReadLead(insights, productId) {
  const read = insights && insights.first_read;
  const id = (insights && insights.first_read_insight_id) || (read && read.id) || '';
  if (!read && !id) return null;
  const title = (read && read.headline) || 'First read';
  const summary = read && read.body ? clipAtWord(String(read.body), 220) : '';
  return el('div', { class: 'dash-first-read' },
    el('div', { class: 'dash-kicker' }, el('span', {}, 'First read')),
    el('button', {
      type: 'button',
      class: 'dash-list-main dash-insight-open',
      onClick: () => openInsightDetail({ id, headline: title, body: (read && read.body) || '' }, productId),
    },
    el('span', { class: 'dash-list-title' }, title),
    summary ? el('span', { class: 'dash-note dash-insight-summary' }, summary) : null),
    insightTools(id, productId));
}

/**
 * Textarea correction and save-as-document. Shown on first-read and in every detail dialog.
 * @param {string} insightId
 * @param {string} [productId]
 */
export function insightTools(insightId, productId) {
  if (!insightId) return null;
  const link = el('p', { class: 'dash-note' });
  const area = el('textarea', {
    class: 'set-textarea',
    rows: 3,
    maxLength: '2000',
    placeholder: 'What should Bonnie remember instead?',
    'aria-label': 'Correction',
  });
  const correct = el('button', {
    type: 'button',
    class: 'btn btn-ghost',
    onClick: () => submitCorrection(insightId, area, correct),
  }, "That's wrong — correct me");
  const save = el('button', {
    type: 'button',
    class: 'btn btn-ghost',
    onClick: () => submitSave(insightId, productId, link, save),
  }, 'Save as document');
  return el('div', { class: 'dash-tools' },
    area,
    el('div', { class: 'dash-tools-row' }, correct, save),
    link);
}

/** @param {object} item @param {string} [productId] */
export function openInsightDetail(item, productId) {
  const preview = insightCardPreview(item);
  const existing = document.querySelector('.dash-insight-detail');
  if (existing) existing.remove();
  const body = el('div', { class: 'dash-insight-detail-body md-body' });
  fillMarkdown(body, String(item.body || item.headline || ''));
  const close = () => {
    document.removeEventListener('keydown', onKey);
    disposeCharts(body);
    body.replaceChildren();
    overlay.remove();
  };
  const onKey = (event) => {
    if (event.key === 'Escape') close();
  };
  const overlay = el('div', {
    class: 'dash-insight-detail',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': preview.title,
  },
  el('button', { type: 'button', class: 'dash-insight-detail-scrim', 'aria-label': 'Close', onClick: close }),
  el('div', { class: 'dash-insight-detail-panel' },
    el('div', { class: 'dash-insight-detail-head' },
      el('h2', { class: 'dash-serif' }, preview.title),
      button('Close', { onClick: close })),
    body,
    insightTools(item.id, productId)));
  document.addEventListener('keydown', onKey);
  document.body.append(overlay);
}

async function submitCorrection(insightId, area, control) {
  const parsed = correctionBody(area.value);
  if (!parsed.ok) {
    toast(parsed.error, 'error');
    return;
  }
  control.disabled = true;
  const result = await api(`/insights/${encodeURIComponent(insightId)}/correct`, {
    method: 'POST',
    body: parsed.body,
  });
  area.value = '';
  control.disabled = false;
  if (!result || result.error) {
    toast((result && result.error) || 'Could not save the correction', 'error');
    return;
  }
  toast('Correction saved to product memory');
}

async function submitSave(insightId, productId, link, control) {
  control.disabled = true;
  const result = await api(`/insights/${encodeURIComponent(insightId)}/save-document`, {
    method: 'POST',
    body: {},
  });
  control.disabled = false;
  const doc = result && result.document;
  if (!result || result.error || !doc || !doc.id) {
    toast((result && result.error) || 'Could not save the document', 'error');
    return;
  }
  link.replaceChildren(
    'Saved. ',
    el('a', {
      href: withProduct(`/app/knowledge?tab=documents&document=${encodeURIComponent(doc.id)}`, productId),
    }, 'Open in Knowledge'));
  toast('Saved to Knowledge');
}
